import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Loader2, MoreVertical, KeyRound, Archive, ArchiveRestore, Users as UsersIcon, Copy, Check, Dice5 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useConfirm } from "../components/ConfirmProvider";

const ROLE_LABEL = { admin: "Admin", pm: "PM", designer: "Designer" };
const ROLE_TINT = {
  admin:     "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]",
  pm:        "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]",
  designer:  "bg-[#F5F3FF] text-[#5B21B6] border-[#DDD6FE]",
};
const FIRM_STAFF_ROLES = [
  { key: "admin",    label: "Admin",    hint: "Full control of the workspace — team, billing, and every project." },
  { key: "pm",       label: "PM",       hint: "Runs projects end-to-end — invite homeowners, contractors, and staff." },
  { key: "designer", label: "Designer", hint: "Works on assigned projects — no team or billing access." },
];

const PW_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
function generatePassword(len = 12) {
  const arr = new Uint32Array(len);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => PW_CHARS[n % PW_CHARS.length]).join("");
}

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canView = ["admin", "pm"].includes(user?.role);
  const confirm = useConfirm();

  const [tab, setTab] = useState("active");        // 'active' | 'archived'
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "designer", password: "" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [temp, setTemp] = useState(null);          // { email, temp_password }

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "designer" });
  const [savingEdit, setSavingEdit] = useState(false);

  const [resetTarget, setResetTarget] = useState(null); // member being reset
  const [resetResult, setResetResult] = useState(null); // { email, temp_password }
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/workspace/members"),
        api.get("/workspace/members", { params: { archived: true } }),
      ]);
      setActive(a.data.members || []);
      setArchived(b.data.members || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [canView]);

  // Designers (and any non-admin/PM staff) get a friendly access-denied panel
  // instead of the roster — the backend also enforces 403 for defence.
  // Designers (and any non-admin/PM staff) get a friendly access-denied panel
  // instead of the roster — the backend also enforces 403 for defence.
  // Placed AFTER all hook calls so React's rules-of-hooks stay happy.
  const members = tab === "active" ? active : archived;
  const counts = useMemo(() => ({ active: active.length, archived: archived.length }), [active, archived]);

  if (!canView) {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <div className="card-soft text-center py-12" data-testid="team-forbidden">
          <div className="w-14 h-14 rounded-full bg-[#F7F4EC] flex items-center justify-center mx-auto mb-4">
            <UsersIcon size={22} className="text-[#7A7571]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#2C2A29]">Team page is admin/PM only</h1>
          <p className="text-sm text-[#7A7571] mt-2">
            Ask your admin or PM if you need someone added or removed.
          </p>
        </div>
      </div>
    );
  }

  const submitInvite = async (e) => {
    e.preventDefault();
    const pw = f.password.trim();
    if (pw && pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/workspace/members", {
        name: f.name.trim(),
        email: f.email.trim().toLowerCase(),
        role: f.role,
        ...(pw ? { password: pw } : {}),
      });
      setTemp(data);
      setF({ name: "", email: "", role: "designer", password: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setInviteBusy(false); }
  };

  const closeInvite = () => {
    setInviteOpen(false);
    setTemp(null);
    setF({ name: "", email: "", role: "designer", password: "" });
  };

  const openEdit = (m) => {
    setEditForm({ name: m.name || "", role: m.role });
    setEditing(m);
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.patch(`/workspace/members/${editing.user_id}`, editForm);
      toast.success("Team member updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingEdit(false); }
  };

  const remove = async (m) => {
    const ok = await confirm({
      title: `Delete ${m.name}?`,
      message: `${m.name} will be permanently removed from this workspace. Historical projects/tasks/activity they created stay intact. This cannot be undone — you may want to Archive instead.`,
      confirmText: "Delete permanently",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/workspace/members/${m.user_id}`);
      toast.success("Team member removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const archive = async (m) => {
    const ok = await confirm({
      title: `Archive ${m.name}?`,
      message: `${m.name} will be signed out and won't be able to log in until you restore them. Their records stay untouched.`,
      confirmText: "Archive",
    });
    if (!ok) return;
    try {
      await api.post(`/workspace/members/${m.user_id}/archive`);
      toast.success("Team member archived");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unarchive = async (m) => {
    try {
      await api.post(`/workspace/members/${m.user_id}/unarchive`);
      toast.success(`${m.name} restored`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startReset = (m) => { setResetTarget(m); setResetResult(null); };
  const doReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/workspace/members/${resetTarget.user_id}/reset-password`);
      setResetResult(data);
      toast.success("New password generated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResetting(false); }
  };

  const copyPw = async (pw) => {
    try { await navigator.clipboard.writeText(pw); toast.success("Copied"); }
    catch { toast.error("Copy failed — select the text manually"); }
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Workspace</div>
          <h1 className="font-display text-4xl font-bold text-[#2C2A29] mt-2">Team</h1>
        </div>
        {isAdmin && (
          <Dialog open={inviteOpen} onOpenChange={(v) => { if (v) setInviteOpen(true); else closeInvite(); }}>
            <DialogTrigger asChild>
              <button data-testid="invite-staff-btn" className="btn-primary inline-flex items-center gap-2">
                <UserPlus size={16} /> Invite staff
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md rounded-3xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Invite team member</DialogTitle>
                <DialogDescription>
                  Create a login for someone at your firm. They&apos;ll be asked to set a new password the first time they sign in.
                </DialogDescription>
              </DialogHeader>
              {temp ? (
                <TempCredentialsPanel temp={temp} onCopy={copyPw} onDone={closeInvite} />
              ) : (
                <form onSubmit={submitInvite} className="space-y-4">
                  <FieldLabel label="Name">
                    <input required className="input-soft" data-testid="staff-name" placeholder="e.g. Alex Chen"
                      value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                  </FieldLabel>
                  <FieldLabel label="Work email">
                    <input required type="email" className="input-soft" data-testid="staff-email" placeholder="alex@yourfirm.com"
                      value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                  </FieldLabel>
                  <FieldLabel label="Temporary password">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input-soft font-mono flex-1"
                        data-testid="staff-password"
                        placeholder="Auto-generated if left blank"
                        value={f.password}
                        onChange={(e) => setF({ ...f, password: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => setF({ ...f, password: generatePassword(12) })}
                        data-testid="staff-genpw"
                        title="Generate a strong random password"
                        className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 flex-shrink-0"
                      >
                        <Dice5 size={16} />
                      </button>
                    </div>
                    <p className="text-[11px] text-[#7A7571] mt-1.5 leading-snug">
                      Leave blank and we&apos;ll generate one for you. Minimum 6 characters.
                    </p>
                  </FieldLabel>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Role</label>
                    <div className="space-y-2">
                      {FIRM_STAFF_ROLES.map((r) => {
                        const selected = f.role === r.key;
                        return (
                          <button
                            key={r.key}
                            type="button"
                            onClick={() => setF({ ...f, role: r.key })}
                            data-testid={`staff-role-${r.key}`}
                            className={`w-full text-left px-4 py-3 rounded-2xl border transition-all ${
                              selected
                                ? "border-[#E07A5F] bg-[#FFF6F1] ring-1 ring-[#E07A5F]/40"
                                : "border-[#EBE5D9] bg-white hover:border-[#E07A5F]/40"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold text-[#2C2A29]">{r.label}</div>
                              {selected && (
                                <span className="text-[10px] uppercase tracking-widest font-bold text-[#E07A5F]">Selected</span>
                              )}
                            </div>
                            <div className="text-xs text-[#7A7571] mt-0.5 leading-snug">{r.hint}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button type="button" onClick={closeInvite} className="btn-secondary flex-1">Cancel</button>
                    <button
                      data-testid="staff-submit"
                      disabled={inviteBusy}
                      className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                    >
                      {inviteBusy && <Loader2 size={16} className="animate-spin" />}
                      {inviteBusy ? "Creating…" : "Create account"}
                    </button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#EBE5D9] mb-6">
        <TabBtn testid="team-tab-active" active={tab === "active"} onClick={() => setTab("active")}
          label="Active" count={counts.active} />
        <TabBtn testid="team-tab-archived" active={tab === "archived"} onClick={() => setTab("archived")}
          label="Archived" count={counts.archived} />
      </div>

      {loading && <p className="text-[#7A7571]">Loading…</p>}
      {!loading && members.length === 0 && (
        <div className="card-soft text-center py-16 text-[#7A7571]" data-testid="team-empty">
          {tab === "active"
            ? "No active team members yet. Invite your first staff to get started."
            : "No archived team members. Archived members will appear here."}
        </div>
      )}

      {/* Full-width member rows */}
      <div className="space-y-2">
        {members.map((m) => (
          <MemberRow
            key={m.user_id}
            m={m}
            isSelf={m.user_id === user?.user_id}
            isAdmin={isAdmin}
            archivedTab={tab === "archived"}
            onEdit={() => openEdit(m)}
            onResetPassword={() => startReset(m)}
            onArchive={() => archive(m)}
            onUnarchive={() => unarchive(m)}
            onDelete={() => remove(m)}
          />
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit team member</DialogTitle>
            <DialogDescription>Update the member&apos;s name and role. Email is read-only.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <FieldLabel label="Email">
              <input className="input-soft opacity-70" value={editing?.email || ""} readOnly />
            </FieldLabel>
            <FieldLabel label="Name">
              <input required className="input-soft" data-testid="edit-member-name"
                value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </FieldLabel>
            <FieldLabel label="Role">
              <select className="input-soft" data-testid="edit-member-role"
                value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                <option value="admin">Admin</option>
                <option value="pm">PM</option>
                <option value="designer">Designer</option>
              </select>
            </FieldLabel>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary flex-1">Cancel</button>
              <button data-testid="edit-member-save" disabled={savingEdit} className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
                {savingEdit && <Loader2 size={16} className="animate-spin" />}
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetResult(null); } }}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reset password</DialogTitle>
            <DialogDescription>
              {resetResult
                ? "Share the temporary password securely. The member will be asked to set a new one at next login."
                : `Generate a new temporary password for ${resetTarget?.name || "this member"}. Their current password will stop working immediately.`}
            </DialogDescription>
          </DialogHeader>
          {resetResult ? (
            <TempCredentialsPanel temp={resetResult} onCopy={copyPw} onDone={() => { setResetTarget(null); setResetResult(null); }} />
          ) : (
            <div className="space-y-3 mt-2">
              <div className="text-xs text-[#7A7571]">
                Member: <b className="text-[#2C2A29]">{resetTarget?.email}</b>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setResetTarget(null)} className="btn-secondary flex-1">Cancel</button>
                <button
                  data-testid="reset-password-confirm"
                  onClick={doReset}
                  disabled={resetting}
                  className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                >
                  {resetting && <Loader2 size={16} className="animate-spin" />}
                  {resetting ? "Generating…" : "Generate new password"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MemberRow({ m, isSelf, isAdmin, archivedTab, onEdit, onResetPassword, onArchive, onUnarchive, onDelete }) {
  const initials = (m.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const roleCls = ROLE_TINT[m.role] || "bg-[#F7F4EC] text-[#2C2A29] border-[#EBE5D9]";
  const showMenu = isAdmin && !isSelf;
  return (
    <div
      data-testid={`member-${m.user_id}`}
      className={`card-soft !p-4 flex items-center gap-4 ${archivedTab ? "opacity-70" : ""}`}
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-[#2C2A29] flex-shrink-0 ${archivedTab ? "bg-[#EBE5D9]" : "bg-[#F2CC8F]"}`}>
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#2C2A29] truncate">
            {m.name} {isSelf && <span className="text-xs font-normal text-[#7A7571]">(you)</span>}
          </span>
          {archivedTab && (
            <span
              data-testid={`member-archived-pill-${m.user_id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#F3E8FF] text-[#6B21A8] border border-[#D8B4FE]"
            >
              <Archive size={10} /> Archived
            </span>
          )}
        </div>
        <div className="text-sm text-[#7A7571] truncate">{m.email}</div>
      </div>
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${roleCls} flex-shrink-0`}>
        {ROLE_LABEL[m.role] || m.role}
      </span>
      {showMenu ? (
        <RowMenu
          m={m}
          archivedTab={archivedTab}
          onEdit={onEdit}
          onResetPassword={onResetPassword}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onDelete={onDelete}
        />
      ) : (
        // Keep column alignment consistent when the menu isn't rendered
        <div className="w-8 flex-shrink-0" />
      )}
    </div>
  );
}

function RowMenu({ m, archivedTab, onEdit, onResetPassword, onArchive, onUnarchive, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`member-menu-${m.user_id}`}
          className="w-8 h-8 flex items-center justify-center rounded-full text-[#7A7571] hover:bg-[#F7F4EC] hover:text-[#2C2A29] flex-shrink-0"
          aria-label="Member actions"
        >
          <MoreVertical size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-2xl border-[#EBE5D9]">
        {!archivedTab && (
          <>
            <DropdownMenuItem data-testid={`member-edit-${m.user_id}`} onClick={onEdit} className="cursor-pointer rounded-xl">
              <Pencil size={14} className="mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`member-reset-${m.user_id}`} onClick={onResetPassword} className="cursor-pointer rounded-xl">
              <KeyRound size={14} className="mr-2" /> Reset password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`member-archive-${m.user_id}`} onClick={onArchive} className="cursor-pointer rounded-xl">
              <Archive size={14} className="mr-2" /> Archive
            </DropdownMenuItem>
          </>
        )}
        {archivedTab && (
          <DropdownMenuItem data-testid={`member-unarchive-${m.user_id}`} onClick={onUnarchive} className="cursor-pointer rounded-xl">
            <ArchiveRestore size={14} className="mr-2" /> Restore
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid={`member-delete-${m.user_id}`}
          onClick={onDelete}
          className="cursor-pointer rounded-xl text-[#9F1239] focus:text-[#9F1239]"
        >
          <Trash2 size={14} className="mr-2" /> Delete permanently
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TabBtn({ label, count, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 transition-colors text-sm font-semibold ${
        active
          ? "border-[#E07A5F] text-[#2C2A29]"
          : "border-transparent text-[#7A7571] hover:text-[#2C2A29]"
      }`}
    >
      <UsersIcon size={14} />
      {label}
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#E07A5F]/15 text-[#E07A5F]" : "bg-[#F7F4EC] text-[#7A7571]"}`}>
        {count}
      </span>
    </button>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function TempCredentialsPanel({ temp, onCopy, onDone }) {
  const [copiedAll, setCopiedAll] = useState(false);
  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`OrkaiFlow — sign-in\nEmail: ${temp.email}\nTemporary password: ${temp.temp_password}`);
      setCopiedAll(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopiedAll(false), 1800);
    } catch { toast.error("Copy failed — select the text manually"); }
  };
  return (
    <div className="space-y-4">
      <div className="card-soft !bg-[#F7F4EC] !p-5" data-testid="temp-credentials-panel">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-3">Share with your new teammate</div>
        <div className="text-xs text-[#7A7571]">Email</div>
        <div className="font-mono text-sm mb-3 break-all select-all" data-testid="temp-email">{temp.email}</div>
        <div className="text-xs text-[#7A7571]">Temporary password</div>
        <div className="flex items-center gap-2">
          <div className="font-mono text-sm select-all flex-1 break-all" data-testid="temp-password">{temp.temp_password}</div>
          <button
            type="button"
            onClick={() => onCopy(temp.temp_password)}
            data-testid="temp-password-copy"
            className="btn-ghost !p-1.5"
            title="Copy password only"
          >
            <Copy size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={copyBoth}
          data-testid="temp-copy-all"
          className="btn-secondary text-sm !py-2 !px-3 mt-4 inline-flex items-center gap-2"
        >
          {copiedAll ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy email + password</>}
        </button>
        <p className="text-[11px] text-[#7A7571] mt-3 leading-snug">
          The member will be prompted to set a new password at first sign-in.
        </p>
      </div>
      <button onClick={onDone} className="btn-primary w-full" data-testid="temp-close">Done</button>
    </div>
  );
}
